import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { Room } from "../schema";

import {
    buildDocumentsModel,
    guessMarketplace,
    type DocumentFile,
    type DocumentsModel,
    type FolderId,
    type RoomStateLike,
    ASSET_FOLDER,
} from "./model";

// Data hooks for the Documents page. The API calls are the ones the legacy
// Uploads page and the Onboarding Checklist already use, plus PATCH.

export const uploadsKey = (slug: string) => ["room-uploads", slug] as const;
export const stateKey = (slug: string) => ["room-state", slug] as const;
export const eventsKey = (slug: string) => ["room-events", slug] as const;

interface ListResponse {
    files: DocumentFile[];
}

async function readError(res: Response, fallback: string): Promise<string> {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    return data.error ?? fallback;
}

export function useRoomUploads(slug: string) {
    return useQuery({
        queryKey: uploadsKey(slug),
        queryFn: async (): Promise<DocumentFile[]> => {
            const res = await fetch(`/api/rooms/${slug}/uploads`, { credentials: "include", cache: "no-store" });
            if (!res.ok) throw new Error(`list failed: ${res.status}`);
            const data = (await res.json()) as ListResponse;
            return data.files;
        },
    });
}

export function useRoomState(slug: string) {
    return useQuery({
        queryKey: stateKey(slug),
        queryFn: async (): Promise<RoomStateLike> => {
            const res = await fetch(`/api/rooms/${slug}/state`, { credentials: "include", cache: "no-store" });
            if (!res.ok) throw new Error(`state failed: ${res.status}`);
            return (await res.json()) as RoomStateLike;
        },
    });
}

export function useDocumentsModel(room: Room): {
    model: DocumentsModel | null;
    files: DocumentFile[];
    loading: boolean;
    error: boolean;
} {
    const uploads = useRoomUploads(room.slug);
    const state = useRoomState(room.slug);
    const files = useMemo(() => uploads.data ?? [], [uploads.data]);
    const model = useMemo(
        () => (uploads.data ? buildDocumentsModel(room, uploads.data, state.data ?? {}) : null),
        [room, uploads.data, state.data],
    );
    return {
        model,
        files,
        loading: uploads.isLoading || state.isLoading,
        error: Boolean(uploads.error),
    };
}

// ─── Mutations ─────────────────────────────────────────────────────────

export interface FilePatch {
    displayName?: string;
    folder?: FolderId | null;
    marketplace?: string | null;
    /** Staff only. `{ note }` sends the file back; `null` clears the note. */
    review?: { note?: string } | null;
}

export function useDocumentActions(room: Room) {
    const slug = room.slug;
    const qc = useQueryClient();
    const refresh = useCallback(() => {
        void qc.invalidateQueries({ queryKey: uploadsKey(slug) });
        void qc.invalidateQueries({ queryKey: stateKey(slug) });
        void qc.invalidateQueries({ queryKey: eventsKey(slug) });
    }, [qc, slug]);

    const patchFile = useMutation({
        mutationFn: async ({ name, patch }: { name: string; patch: FilePatch }) => {
            const res = await fetch(`/api/rooms/${slug}/uploads/${encodeURIComponent(name)}`, {
                method: "PATCH",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(patch),
            });
            if (!res.ok) throw new Error(await readError(res, `update failed (${res.status})`));
            return (await res.json()) as DocumentFile;
        },
        onSuccess: refresh,
    });

    const removeFile = useMutation({
        mutationFn: async (name: string) => {
            const res = await fetch(`/api/rooms/${slug}/uploads/${encodeURIComponent(name)}`, {
                method: "DELETE",
                credentials: "include",
            });
            if (!res.ok) throw new Error(await readError(res, `remove failed (${res.status})`));
        },
        onSuccess: refresh,
    });

    // Accepting a request is ticking its Onboarding Plan task; the same POST the
    // plan's own toggle makes, so the two pages can never disagree.
    const setTaskStatus = useMutation({
        mutationFn: async ({ taskKey, complete }: { taskKey: string; complete: boolean }) => {
            const res = await fetch(`/api/rooms/${slug}/state`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ taskStatus: { [taskKey]: complete ? "complete" : "pending" } }),
            });
            if (!res.ok) throw new Error(await readError(res, `update failed (${res.status})`));
        },
        onSuccess: refresh,
    });

    // A supplier-made folder is one key in room state (id → label).
    const createFolder = useMutation({
        mutationFn: async ({ id, label }: { id: FolderId; label: string }) => {
            const res = await fetch(`/api/rooms/${slug}/state`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ documentFolders: { [id]: label } }),
            });
            if (!res.ok) throw new Error(await readError(res, `could not create the folder (${res.status})`));
        },
        onSuccess: refresh,
    });

    return { patchFile, removeFile, setTaskStatus, createFolder, refresh };
}

// ─── Upload queue with per-file progress ───────────────────────────────

export interface UploadOptions {
    folder?: FolderId | null;
    /** Label of `folder`, for the queue row. */
    folderLabel?: string;
    taskKey?: string;
    marketplace?: string | null;
}

export type QueueStatus = "queued" | "uploading" | "done" | "failed";

export interface QueueItem {
    id: string;
    file: File;
    options: UploadOptions;
    status: QueueStatus;
    progress: number; // 0..1
    error?: string;
    result?: DocumentFile;
}

function uploadWithProgress(
    slug: string,
    file: File,
    options: UploadOptions,
    onProgress: (share: number) => void,
): Promise<DocumentFile> {
    return new Promise((resolve, reject) => {
        const form = new FormData();
        form.append("file", file, file.name);
        if (options.folder) form.append("folder", options.folder);
        if (options.taskKey) form.append("taskKey", options.taskKey);
        if (options.marketplace) form.append("marketplace", options.marketplace);

        const xhr = new XMLHttpRequest();
        xhr.open("POST", `/api/rooms/${slug}/uploads`);
        xhr.withCredentials = true;
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) onProgress(e.loaded / e.total);
        };
        xhr.onerror = () => reject(new Error("The upload did not go through. Check your connection and try again."));
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    resolve(JSON.parse(xhr.responseText) as DocumentFile);
                } catch {
                    reject(new Error("The upload finished but the reply could not be read."));
                }
                return;
            }
            let message = `upload failed (${xhr.status})`;
            try {
                const data = JSON.parse(xhr.responseText) as { error?: string };
                if (data.error) message = data.error;
            } catch {
                // keep the status message
            }
            reject(new Error(message));
        };
        xhr.send(form);
    });
}

let queueSeq = 0;

/**
 * Sequential upload queue. Every file gets its own row with progress, and a
 * failed file stays visible with a retry, so a partial batch is never mistaken
 * for a complete one. A file uploaded against an Onboarding Plan task is also
 * attached to that task (same call the plan's own upload button makes).
 */
export function useUploadQueue(room: Room, loaMarketplaces: readonly string[]) {
    const slug = room.slug;
    const qc = useQueryClient();
    // The ref is the working copy (the pump reads it between awaits); state
    // mirrors it for rendering.
    const itemsRef = useRef<QueueItem[]>([]);
    const [items, setItems] = useState<QueueItem[]>([]);
    const running = useRef(false);

    const commit = useCallback((next: QueueItem[]) => {
        itemsRef.current = next;
        setItems(next);
    }, []);

    const update = useCallback(
        (id: string, patch: Partial<QueueItem>) => {
            commit(itemsRef.current.map((it) => (it.id === id ? { ...it, ...patch } : it)));
        },
        [commit],
    );

    const pump = useCallback(async () => {
        if (running.current) return;
        running.current = true;
        try {
            for (;;) {
                const item = itemsRef.current.find((it) => it.status === "queued");
                if (!item) break;
                update(item.id, { status: "uploading", progress: 0 });
                try {
                    const options: UploadOptions = { ...item.options };
                    if (options.folder === "loa" && !options.marketplace) {
                        options.marketplace = guessMarketplace(item.file.name, loaMarketplaces);
                    }
                    const result = await uploadWithProgress(slug, item.file, options, (share) =>
                        update(item.id, { progress: share }),
                    );
                    if (options.taskKey) {
                        await fetch(`/api/rooms/${slug}/state`, {
                            method: "POST",
                            credentials: "include",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ taskAttachments: { [options.taskKey]: { add: [result.name] } } }),
                        }).catch(() => undefined);
                    }
                    update(item.id, { status: "done", progress: 1, result });
                    void qc.invalidateQueries({ queryKey: uploadsKey(slug) });
                    void qc.invalidateQueries({ queryKey: stateKey(slug) });
                    void qc.invalidateQueries({ queryKey: eventsKey(slug) });
                } catch (err) {
                    update(item.id, { status: "failed", error: (err as Error).message });
                }
            }
        } finally {
            running.current = false;
        }
    }, [loaMarketplaces, qc, slug, update]);

    const enqueue = useCallback(
        (files: Iterable<File>, options: UploadOptions | ((file: File) => UploadOptions)) => {
            const fresh: QueueItem[] = Array.from(files).map((file) => ({
                id: `u${(queueSeq += 1)}`,
                file,
                options: typeof options === "function" ? options(file) : options,
                status: "queued",
                progress: 0,
            }));
            if (fresh.length === 0) return;
            commit([...itemsRef.current, ...fresh]);
            void pump();
        },
        [commit, pump],
    );

    const retry = useCallback(
        (id: string) => {
            update(id, { status: "queued", progress: 0, error: undefined });
            void pump();
        },
        [pump, update],
    );
    const dismiss = useCallback((id: string) => commit(itemsRef.current.filter((it) => it.id !== id)), [commit]);
    const clearDone = useCallback(() => commit(itemsRef.current.filter((it) => it.status !== "done")), [commit]);

    // A batch that finished with nothing failed clears itself after a few
    // seconds (2026-10-27, Elijah); failed rows stay until retried or dismissed.
    const allDone = items.length > 0 && items.every((it) => it.status === "done");
    useEffect(() => {
        if (!allDone) return;
        const timer = setTimeout(clearDone, 3000);
        return () => clearTimeout(timer);
    }, [allDone, clearDone]);

    return { items, enqueue, retry, dismiss, clearDone };
}

// ─── Activity log ──────────────────────────────────────────────────────

/** One entry of the room's event log (GET /api/rooms/{slug}/events). */
export interface RoomEvent {
    type: string;
    payload?: Record<string, unknown>;
    at: string;
    by?: string;
}

const DOCUMENT_EVENT_TYPES = new Set([
    "document_uploaded",
    "document_replaced",
    "document_renamed",
    "document_moved",
    "document_removed",
    "document_returned",
    "document_review_cleared",
    "folder_created",
    "folder_renamed",
    "folder_removed",
]);

/** Every change made on the Documents page, newest first. */
export function useDocumentActivity(slug: string) {
    return useQuery({
        queryKey: eventsKey(slug),
        queryFn: async (): Promise<RoomEvent[]> => {
            const res = await fetch(`/api/rooms/${slug}/events`, { credentials: "include", cache: "no-store" });
            if (!res.ok) throw new Error(`events failed: ${res.status}`);
            const data = (await res.json()) as { events?: RoomEvent[] };
            // Room assets (page media) are not documents: their events never
            // show, including the ones logged before the API stopped writing them.
            return (data.events ?? [])
                .filter((e) => DOCUMENT_EVENT_TYPES.has(e.type) && e.payload?.folder !== ASSET_FOLDER)
                .sort((a, b) => b.at.localeCompare(a.at));
        },
    });
}
